import * as fx from './fixtures'

// Client-side fake API. Installed as the axios adapter on both HTTP instances
// in demo mode (see http-common.js), so no request ever leaves the browser.
// Each route matches on method + URL and returns data from the fixtures.

function buildResponse (config, data, status) {
  return {
    data: data,
    status: status || 200,
    statusText: 'OK',
    headers: {},
    config: config,
    request: {}
  }
}

function parseUrl (url) {
  let clean = String(url || '')
  // axios may hand the adapter the full URL (baseURL + path); normalize to the
  // path relative to the API base so route matching works either way.
  clean = clean.replace(/^https?:\/\/[^/]+/, '')
  clean = clean.replace(/^\/?(v2|v1|api\/v1|api)\//, '')
  clean = clean.replace(/^\//, '')
  const qIndex = clean.indexOf('?')
  const path = qIndex >= 0 ? clean.slice(0, qIndex) : clean
  const query = {}
  if (qIndex >= 0) {
    clean.slice(qIndex + 1).split('&').forEach(function (pair) {
      const eq = pair.indexOf('=')
      const key = eq >= 0 ? pair.slice(0, eq) : pair
      const value = eq >= 0 ? pair.slice(eq + 1) : ''
      if (key) {
        query[decodeURIComponent(key)] = decodeURIComponent(value)
      }
    })
  }
  return { path: path, query: query, segments: path.split('/').filter(Boolean) }
}

function parseBody (config) {
  if (!config.data) {
    return {}
  }
  if (typeof config.data === 'string') {
    try {
      return JSON.parse(config.data)
    } catch (err) {
      return {}
    }
  }
  return config.data
}

// Route table: first match wins. `match(ctx)` returns a boolean; `handle(ctx)`
// returns the response body.
const routes = [
  { method: 'get', match: function (c) { return c.path === 'properties' }, handle: function () { return fx.getProperties() } },
  { method: 'get', match: function (c) { return c.path === 'nuclei' }, handle: function () { return fx.getNuclei() } },
  { method: 'get', match: function (c) { return c.path === 'barns' || c.segments[0] === 'barns' }, handle: function () { return fx.getBarns() } },
  { method: 'post', match: function (c) { return c.path === 'devices/realtime' }, handle: function () { return fx.getDevicesRealtime() } },
  { method: 'get', match: function (c) { return c.path === 'devices/features' }, handle: function () { return fx.getDeviceFeatures() } },
  { method: 'get', match: function (c) { return c.path === 'devices' }, handle: function () { return fx.getDevices() } },
  { method: 'get', match: function (c) { return /^batches\/[^/]+\/weight_analysis$/.test(c.path) }, handle: function (c) { return fx.getWeightAnalysis(c.segments[1]) } },
  { method: 'get', match: function (c) { return c.path === 'batches' }, handle: function (c) { return fx.getBatchesByBarn(c.query.barnId) } },
  { method: 'post', match: function (c) { return c.path === 'bird-weight/projection' }, handle: function (c) { return fx.getProjection(c.body.batchId, c.body.days) } },
  { method: 'get', match: function (c) { return c.path === 'mortalities' }, handle: function (c) { return fx.getMortalities(c.query.batchId) } },
  { method: 'get', match: function (c) { return /^silos\/[^/]+$/.test(c.path) }, handle: function (c) { return fx.getSilos(c.segments[1]) } },
  { method: 'get', match: function (c) { return c.path === 'breed-growth-curves' }, handle: function () { return fx.getBreedCurves() } },
  { method: 'get', match: function (c) { return c.path === 'translators/alarms' }, handle: function () { return fx.getAlarmTranslators() } },
  { method: 'get', match: function (c) { return c.path === 'statistics' }, handle: function (c) { return fx.getStatistics(c.query.mac) } },
  { method: 'get', match: function (c) { return c.path === 'inobramx/maxmin' }, handle: function (c) { return fx.getInobramxMaxMin(c.query.mac, c.query.location === 'external') } },
  { method: 'get', match: function (c) { return c.path === 'inobramx/setpoint' }, handle: function (c) { return fx.getInobramxSetpoint(c.query.mac, c.query.metric) } },
  { method: 'get', match: function (c) { return c.path === 'inobramx/performance' }, handle: function (c) { return fx.getInobramxWaterConsumption(c.query.mac) } },
  { method: 'get', match: function (c) { return c.path === 'alarms' }, handle: function (c) { return fx.getAlarmHistory(c.query) } },
  { method: 'post', match: function (c) { return c.path === 'fcm/tokens' }, handle: function () { return {} } },
  { method: 'post', match: function (c) { return c.path === 'subscription-devices' }, handle: function () { return {} } }
]

function defaultFor (method) {
  return method === 'get' ? [] : {}
}

function safeMatch (route, ctx) {
  try {
    return route.match(ctx)
  } catch (err) {
    return false
  }
}

// axios adapter signature: (config) => Promise<response>
export function demoAdapter (config) {
  const method = (config.method || 'get').toLowerCase()
  const ctx = parseUrl(config.url)
  // axios passes request params to a custom adapter via `config.params`
  // (buildURL only runs inside the default adapters), so merge them into the
  // parsed query. URL query params still win if both are present.
  if (config.params && typeof config.params === 'object') {
    Object.keys(config.params).forEach(function (key) {
      const value = config.params[key]
      if (value !== null && value !== undefined && ctx.query[key] === undefined) {
        ctx.query[key] = String(value)
      }
    })
  }
  ctx.method = method
  ctx.body = parseBody(config)

  const route = routes.find(function (r) {
    return r.method === method && safeMatch(r, ctx)
  })

  let data
  try {
    data = route ? route.handle(ctx) : defaultFor(method)
  } catch (err) {
    data = defaultFor(method)
  }

  return Promise.resolve(buildResponse(config, data))
}



// WEBPACK FOOTER //
// ./src/demo/mock-api.js